/**
 * MealPlanViewer — Phase 2.2B Session 4
 *
 * Read-only renderer for a meal-plan-version document. Used by:
 *   • Staff dashboard to inspect the current version
 *   • Athlete page to render today's resolved plan
 */
import React from 'react';
import { Utensils } from 'lucide-react';
import { Card } from '../ui/card';
import { useLanguage } from '../../context/LanguageContext';

const fmt1 = (v) => v === null || v === undefined ? '—' : Number(v).toFixed(1);
const fmt0 = (v) => v === null || v === undefined ? '—' : Math.round(Number(v));

const MealPlanViewer = ({ version, emptyMessage }) => {
  const { language } = useLanguage();
  if (!version || !(version.meals || []).length) {
    return (
      <Card className="bg-zinc-900 border-zinc-800 p-5 text-center text-sm text-zinc-500" data-testid="mealplan-viewer-empty">
        {emptyMessage || (language === 'IT' ? 'Nessun pasto definito.' : 'No meals defined.')}
      </Card>
    );
  }

  return (
    <Card className="bg-zinc-900 border-zinc-800 p-5 space-y-3" data-testid="mealplan-viewer">
      <h3 className="text-lg font-semibold text-zinc-100 flex items-center gap-2">
        <Utensils className="w-5 h-5 text-amber-400" />
        {language === 'IT' ? 'Pasti' : 'Meals'}
      </h3>
      {version.meals.map((m, idx) => {
        const mealKcal = (m.foods || []).reduce((s, f) => s + (Number(f.kcal) || 0), 0);
        return (
          <div key={idx} className="bg-zinc-950 border border-zinc-800 rounded p-3" data-testid={`viewer-meal-${m.slot}`}>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold text-zinc-200 uppercase tracking-wider">{(m.slot || '').replace('_', ' ')}</h4>
              <span className="text-xs text-zinc-500" data-testid={`viewer-meal-kcal-${m.slot}`}>
                {fmt0(mealKcal)} kcal
              </span>
            </div>
            {m.meal_notes && <div className="text-[11px] text-zinc-500 italic mb-2">{m.meal_notes}</div>}
            <table className="w-full text-xs">
              <thead className="text-zinc-500">
                <tr>
                  <th className="text-left py-1 font-normal">{language === 'IT' ? 'Alimento' : 'Food'}</th>
                  <th className="text-right py-1 font-normal w-14">g</th>
                  <th className="text-right py-1 font-normal w-14">kcal</th>
                  <th className="text-right py-1 font-normal w-12">P</th>
                  <th className="text-right py-1 font-normal w-12">C</th>
                  <th className="text-right py-1 font-normal w-12">F</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800">
                {(m.foods || []).map((f, fi) => (
                  <tr key={fi} data-testid={`viewer-food-${m.slot}-${fi}`}>
                    <td className="py-1 text-zinc-200">{f.name}</td>
                    <td className="py-1 text-right text-zinc-300">{fmt1(f.qty_g)}</td>
                    <td className="py-1 text-right text-zinc-100">{fmt0(f.kcal)}</td>
                    <td className="py-1 text-right text-zinc-300">{fmt1(f.protein_g)}</td>
                    <td className="py-1 text-right text-zinc-300">{fmt1(f.carbs_g)}</td>
                    <td className="py-1 text-right text-zinc-300">{fmt1(f.fats_g)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
      {version.notes && (
        <div className="text-xs text-zinc-500 italic border-t border-zinc-800 pt-3" data-testid="viewer-notes">
          {version.notes}
        </div>
      )}
    </Card>
  );
};

export default MealPlanViewer;
